/**
 * Invoice line edits (finance request 2026-09-10, item 8 — "edit the invoice
 * directly from Review Invoice").
 *
 * The finance team wants to open an invoice and change individual lines —
 * description, contractor, hours, quantity, rate, amount, date — and to add or
 * remove lines, without going back through the event workflow. Decision (Jhony,
 * 2026-09-16): the edits live ON THE INVOICE ONLY. Shifts, POs, SOWs and the
 * contractor pay are never touched; an edited line is what the client sees,
 * nothing more. Editing a generated invoice still produces a NEW REVISION — the
 * revision that was sent is never mutated.
 *
 * How it plugs into the engine (`table/invoiceTable.ts#getInvoiceRows`):
 *   - every rendered line gets a stable `lineKey` (session + shift group + row
 *     index) so an override can be matched again when the invoice is re-opened;
 *   - an override patches the rendered line (`original` keeps the engine's
 *     figures) and the money follows the line: the totals are the legacy totals
 *     PLUS the delta of every edited / removed / added line, so a description
 *     change never moves a cent and a rate change moves exactly that line;
 *   - extra lines are printed in a trailing "Additional items" section;
 *   - removed lines disappear from the PDF / CSV (`includeRemoved` keeps them,
 *     flagged, for the editor).
 *
 * Pure module, part of the shared engine: the web app, the pdf-service bundle
 * and the backend bundle validate and compute with the very same code.
 */

export interface InvoiceLineOverride {
  /** `lineKey` of the rendered line being edited (opaque, engine-generated). */
  key: string;
  description?: string;
  contractor?: string;
  note?: string;
  /** YYYY-MM-DD — moves the whole shift group to that day on the invoice. */
  date?: string;
  hours?: number;
  people?: number;
  rate?: number;
  /** Explicit amount; when absent an edited hours/people/rate recomputes hours × rate × people. */
  amount?: number;
  removed?: boolean;
}

export interface InvoiceExtraLine {
  /** Client-generated id (kept so the editor can address the line). */
  id: string;
  /** YYYY-MM-DD printed in the Date column. */
  date?: string;
  description: string;
  contractor?: string;
  note?: string;
  hours: number;
  people: number;
  rate: number;
  amount: number;
  /** Default true — the line joins the taxable base like labor does. */
  taxable: boolean;
}

export interface InvoiceLineEdits {
  overrides: InvoiceLineOverride[];
  extras: InvoiceExtraLine[];
}

export const INVOICE_LINE_EDIT_LIMITS = Object.freeze({
  maxOverrides: 300,
  maxExtras: 100,
  maxText: 300,
  maxKey: 1000,
  maxHours: 10_000,
  maxPeople: 10_000,
  maxMoney: 1_000_000,
});

export const INVOICE_LINE_EDIT_ERRORS = Object.freeze({
  shape: "Invoice line edits must be an object with overrides and extras",
  tooMany: `Too many invoice line edits (max ${INVOICE_LINE_EDIT_LIMITS.maxOverrides} edited lines and ${INVOICE_LINE_EDIT_LIMITS.maxExtras} extra lines)`,
  key: "Every edited invoice line needs its line key",
  text: `Invoice line text fields must be at most ${INVOICE_LINE_EDIT_LIMITS.maxText} characters`,
  date: "Invoice line dates must be YYYY-MM-DD",
  hours: `Invoice line hours must be a number between 0 and ${INVOICE_LINE_EDIT_LIMITS.maxHours}`,
  people: `Invoice line quantity must be a whole number between 0 and ${INVOICE_LINE_EDIT_LIMITS.maxPeople}`,
  rate: `Invoice line rate must be a number between 0 and $${INVOICE_LINE_EDIT_LIMITS.maxMoney.toLocaleString("en-US")}`,
  amount: `Invoice line amount must be a number between 0 and $${INVOICE_LINE_EDIT_LIMITS.maxMoney.toLocaleString("en-US")}`,
  extraDescription: "Every extra invoice line needs a description",
  extraId: "Every extra invoice line needs an id",
});

export const INVOICE_EXTRA_SESSION_ID = "invoice-extra-lines";
export const INVOICE_EXTRA_SESSION_NAME = "Additional items";

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function roundLineMoney(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === "";

const cleanText = (value: unknown, errors: string[]): string | undefined => {
  if (value === undefined || value === null) return undefined;
  const text = String(value).replace(/\s+/g, " ").trim();
  if (text.length > INVOICE_LINE_EDIT_LIMITS.maxText) {
    errors.push(INVOICE_LINE_EDIT_ERRORS.text);
    return undefined;
  }
  return text;
};

const cleanDate = (value: unknown, errors: string[]): string | undefined => {
  if (isBlank(value)) return undefined;
  const text = String(value).trim();
  if (!DATE_ONLY.test(text)) {
    errors.push(INVOICE_LINE_EDIT_ERRORS.date);
    return undefined;
  }
  return text;
};

const cleanNumber = (
  value: unknown,
  errors: string[],
  message: string,
  max: number,
  integer = false,
): number | undefined => {
  if (isBlank(value)) return undefined;
  const n = toFiniteNumber(value);
  if (n === null || n < 0 || n > max || (integer && !Number.isInteger(n))) {
    errors.push(message);
    return undefined;
  }
  return integer ? n : roundLineMoney(n);
};

const dedupe = (errors: string[]): string[] => Array.from(new Set(errors));

/**
 * Turns whatever the client posted into clean `{ overrides, extras }` (or
 * `null` when nothing is edited). Never throws: the API answers 400 with
 * `errors`, the editor shows them inline. Overrides that change nothing are
 * dropped so the document only records real edits.
 */
export function normalizeInvoiceLineEdits(raw: unknown): {
  lineEdits: InvoiceLineEdits | null;
  errors: string[];
} {
  if (raw === null || raw === undefined) return { lineEdits: null, errors: [] };
  if (typeof raw !== "object" || Array.isArray(raw)) {
    return { lineEdits: null, errors: [INVOICE_LINE_EDIT_ERRORS.shape] };
  }
  const obj = raw as { overrides?: unknown; extras?: unknown };
  const rawOverrides = obj.overrides === undefined || obj.overrides === null ? [] : obj.overrides;
  const rawExtras = obj.extras === undefined || obj.extras === null ? [] : obj.extras;
  if (!Array.isArray(rawOverrides) || !Array.isArray(rawExtras)) {
    return { lineEdits: null, errors: [INVOICE_LINE_EDIT_ERRORS.shape] };
  }
  if (
    rawOverrides.length > INVOICE_LINE_EDIT_LIMITS.maxOverrides ||
    rawExtras.length > INVOICE_LINE_EDIT_LIMITS.maxExtras
  ) {
    return { lineEdits: null, errors: [INVOICE_LINE_EDIT_ERRORS.tooMany] };
  }

  const errors: string[] = [];
  const overrides: InvoiceLineOverride[] = [];
  const seenKeys = new Set<string>();
  for (const item of rawOverrides) {
    if (!item || typeof item !== "object") {
      errors.push(INVOICE_LINE_EDIT_ERRORS.shape);
      continue;
    }
    const o = item as Record<string, unknown>;
    const key = String(o.key ?? "").trim();
    if (!key || key.length > INVOICE_LINE_EDIT_LIMITS.maxKey) {
      errors.push(INVOICE_LINE_EDIT_ERRORS.key);
      continue;
    }
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    const override: InvoiceLineOverride = { key };
    const description = cleanText(o.description, errors);
    if (description !== undefined) override.description = description;
    const contractor = cleanText(o.contractor, errors);
    if (contractor !== undefined) override.contractor = contractor;
    const note = cleanText(o.note, errors);
    if (note !== undefined) override.note = note;
    const date = cleanDate(o.date, errors);
    if (date !== undefined) override.date = date;
    const hours = cleanNumber(o.hours, errors, INVOICE_LINE_EDIT_ERRORS.hours, INVOICE_LINE_EDIT_LIMITS.maxHours);
    if (hours !== undefined) override.hours = hours;
    const people = cleanNumber(
      o.people,
      errors,
      INVOICE_LINE_EDIT_ERRORS.people,
      INVOICE_LINE_EDIT_LIMITS.maxPeople,
      true,
    );
    if (people !== undefined) override.people = people;
    const rate = cleanNumber(o.rate, errors, INVOICE_LINE_EDIT_ERRORS.rate, INVOICE_LINE_EDIT_LIMITS.maxMoney);
    if (rate !== undefined) override.rate = rate;
    const amount = cleanNumber(o.amount, errors, INVOICE_LINE_EDIT_ERRORS.amount, INVOICE_LINE_EDIT_LIMITS.maxMoney);
    if (amount !== undefined) override.amount = amount;
    if (o.removed === true || o.removed === "true") override.removed = true;
    // Nothing edited on this line → not an override.
    if (Object.keys(override).length > 1) overrides.push(override);
  }

  const extras: InvoiceExtraLine[] = [];
  const seenIds = new Set<string>();
  for (const item of rawExtras) {
    if (!item || typeof item !== "object") {
      errors.push(INVOICE_LINE_EDIT_ERRORS.shape);
      continue;
    }
    const e = item as Record<string, unknown>;
    const id = String(e.id ?? "").trim();
    if (!id || id.length > 64) {
      errors.push(INVOICE_LINE_EDIT_ERRORS.extraId);
      continue;
    }
    if (seenIds.has(id)) continue;
    seenIds.add(id);
    const description = cleanText(e.description, errors) ?? "";
    if (!description) errors.push(INVOICE_LINE_EDIT_ERRORS.extraDescription);
    const hours =
      cleanNumber(e.hours, errors, INVOICE_LINE_EDIT_ERRORS.hours, INVOICE_LINE_EDIT_LIMITS.maxHours) ?? 1;
    const people =
      cleanNumber(e.people, errors, INVOICE_LINE_EDIT_ERRORS.people, INVOICE_LINE_EDIT_LIMITS.maxPeople, true) ??
      1;
    const rate = cleanNumber(e.rate, errors, INVOICE_LINE_EDIT_ERRORS.rate, INVOICE_LINE_EDIT_LIMITS.maxMoney) ?? 0;
    const explicitAmount = cleanNumber(
      e.amount,
      errors,
      INVOICE_LINE_EDIT_ERRORS.amount,
      INVOICE_LINE_EDIT_LIMITS.maxMoney,
    );
    const amount = explicitAmount ?? roundLineMoney(hours * rate * people);
    const extra: InvoiceExtraLine = {
      id,
      description,
      hours,
      people,
      rate,
      amount,
      taxable: !(e.taxable === false || e.taxable === "false"),
    };
    const date = cleanDate(e.date, errors);
    if (date !== undefined) extra.date = date;
    const contractor = cleanText(e.contractor, errors);
    if (contractor) extra.contractor = contractor;
    const note = cleanText(e.note, errors);
    if (note) extra.note = note;
    extras.push(extra);
  }

  const uniqueErrors = dedupe(errors);
  if (uniqueErrors.length) return { lineEdits: null, errors: uniqueErrors };
  if (!overrides.length && !extras.length) return { lineEdits: null, errors: [] };
  return { lineEdits: { overrides, extras }, errors: [] };
}

export function hasInvoiceLineEdits(input: InvoiceLineEdits | null | undefined): boolean {
  return Boolean(input && ((input.overrides && input.overrides.length) || (input.extras && input.extras.length)));
}

/** Stable key of a rendered invoice line: session + shift group + sub-row index. */
export function buildInvoiceLineKey(sessionId: unknown, groupKey: unknown, idx: number): string {
  return `${String(sessionId ?? "")}::${String(groupKey ?? "")}::${idx}`;
}

export function buildInvoiceExtraLineKey(id: string): string {
  return `extra::${id}`;
}

/** Original figures of a line before the override was applied (what the engine computed). */
export interface InvoiceLineOriginal {
  description: string;
  contractor: string;
  note: string;
  hours: number;
  people: number;
  appliedRate: number | null;
  estimate: number;
}

/**
 * Applies one override to one rendered sub-row. Returns the patched row plus
 * the money delta it introduces (new amount − engine amount). A removed line
 * contributes −original.
 */
export function applyInvoiceLineOverride(
  row: {
    description?: string;
    contractor?: string;
    note?: string;
    hours?: unknown;
    people?: unknown;
    appliedRate?: number | null;
    estimate?: unknown;
  },
  override: InvoiceLineOverride | undefined,
  lineKey: string,
): { row: Record<string, unknown>; delta: number; removed: boolean; edited: boolean } {
  const originalEstimate = roundLineMoney(row.estimate);
  const original: InvoiceLineOriginal = {
    description: String(row.description ?? ""),
    contractor: String(row.contractor ?? ""),
    note: String(row.note ?? ""),
    hours: Number(row.hours) || 0,
    people: Number(row.people) || 0,
    appliedRate: row.appliedRate === null || row.appliedRate === undefined ? null : Number(row.appliedRate),
    estimate: originalEstimate,
  };
  if (!override) {
    return { row: { ...row, lineKey }, delta: 0, removed: false, edited: false };
  }
  if (override.removed) {
    return {
      row: { ...row, lineKey, original, edited: true, removed: true, estimate: 0 },
      delta: -originalEstimate,
      removed: true,
      edited: true,
    };
  }
  const hours = override.hours ?? original.hours;
  const people = override.people ?? original.people;
  const rate =
    override.rate !== undefined ? override.rate : original.appliedRate === null ? null : original.appliedRate;
  const quantityChanged =
    override.hours !== undefined || override.people !== undefined || override.rate !== undefined;
  let estimate = originalEstimate;
  if (override.amount !== undefined) {
    estimate = override.amount;
  } else if (quantityChanged) {
    estimate = roundLineMoney(hours * (rate ?? 0) * people);
  }
  return {
    row: {
      ...row,
      lineKey,
      original,
      edited: true,
      description: override.description !== undefined ? override.description : row.description,
      contractor: override.contractor !== undefined ? override.contractor : row.contractor,
      note: override.note !== undefined ? override.note : row.note,
      hours,
      people,
      appliedRate: rate,
      estimate,
    },
    delta: roundLineMoney(estimate - originalEstimate),
    removed: false,
    edited: true,
  };
}

/**
 * The trailing "Additional items" session that carries the extra lines, in the
 * same shape the invoice renderers (screen, PDF, CSV) read for every session.
 */
export function buildInvoiceExtraSession(
  extras: InvoiceExtraLine[],
  fallbackDate?: string,
): {
  session: Record<string, unknown> | null;
  taxableTotal: number;
  nonTaxableTotal: number;
} {
  if (!extras || !extras.length) return { session: null, taxableTotal: 0, nonTaxableTotal: 0 };
  let taxableTotal = 0;
  let nonTaxableTotal = 0;
  const positions = extras.map((extra) => {
    const amount = roundLineMoney(extra.amount);
    if (extra.taxable) taxableTotal = roundLineMoney(taxableTotal + amount);
    else nonTaxableTotal = roundLineMoney(nonTaxableTotal + amount);
    const date = extra.date || fallbackDate || "";
    return {
      _id: buildInvoiceExtraLineKey(extra.id),
      isExtraLine: true,
      extraId: extra.id,
      positionName: extra.description,
      startDate: date,
      date,
      taxable: extra.taxable,
      subSection: [
        {
          lineKey: buildInvoiceExtraLineKey(extra.id),
          isExtraLine: true,
          extraId: extra.id,
          appliedRate: extra.rate,
          description: "",
          hours: extra.hours,
          people: extra.people,
          estimate: amount,
          contractor: extra.contractor || "",
          note: extra.note || "",
          taxable: extra.taxable,
        },
      ],
    };
  });
  const total = roundLineMoney(taxableTotal + nonTaxableTotal);
  const lastDate = positions[positions.length - 1]?.startDate;
  const subtotalRow: Record<string, unknown> = {
    isSubtotal: true,
    estimate: total,
    date: lastDate,
    startDate: lastDate,
  };
  positions.push(subtotalRow as (typeof positions)[number]);
  return {
    session: {
      _id: INVOICE_EXTRA_SESSION_ID,
      sessionName: INVOICE_EXTRA_SESSION_NAME,
      isExtraSession: true,
      subTotal: total.toFixed(2),
      subTotalOTDT: 0,
      positions,
    },
    taxableTotal,
    nonTaxableTotal,
  };
}
